import React, { useMemo, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Search, FileText, Wrench, Eye, Trash2, X, User, Phone, Car, Gauge,
  Fuel, UserCog, Calendar, ClipboardList, Package, AlertTriangle, Filter, Download,
  Megaphone, Save, Loader2, Camera,
} from "lucide-react";
import {
  StatCard, StatusBadge, FilterPill, SectionCard, ActionButton, EmptyState,
  SearchInput, ATELIER_STATUS,
} from "./AdminUI";
import { buildImageUrl } from "../components/depotvente/DepotVenteListingForm";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_META_FIELDS = [
  { key: "page_title", label: "Titre de la page", placeholder: "Bienvenue Kevin" },
  { key: "welcome_top", label: "Message de bienvenue", placeholder: "Bienvenue sur ton espace atelier" },
  { key: "section_intakes", label: "Titre : liste des fiches", placeholder: "Fiches de prise en charge" },
  { key: "section_new", label: "Titre : nouvelle fiche", placeholder: "Nouvelle prise en charge" },
  { key: "section_edit", label: "Titre : édition fiche", placeholder: "Modifier la fiche" },
  { key: "empty_intakes", label: "État vide (aucune fiche)", placeholder: "Aucune fiche pour le moment" },
  { key: "cta_new", label: "Libellé du bouton d'ajout", placeholder: "Nouvelle fiche" },
];

const AtelierTab = ({ ctx }) => {
  const {
    atelierIntakes, atelierSearch, setAtelierSearch, atelierStatusFilter, setAtelierStatusFilter,
    fetchAtelierIntakes, selectedAtelierIntake, setSelectedAtelierIntake,
    technicianPassword, newTechnicianPassword, setNewTechnicianPassword, updateTechnicianPassword,
    generateIntakePDF, deleteAtelierIntake, technicians,
  } = ctx;

  const [technicianFilter, setTechnicianFilter] = React.useState("all");
  const [pageMeta, setPageMeta] = useState({
    page_title: "", welcome_top: "", section_intakes: "", section_new: "",
    section_edit: "", empty_intakes: "", cta_new: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/atelier/page-meta`)
      .then((r) => setPageMeta({
        page_title: r.data.page_title || "",
        welcome_top: r.data.welcome_top || "",
        section_intakes: r.data.section_intakes || "",
        section_new: r.data.section_new || "",
        section_edit: r.data.section_edit || "",
        empty_intakes: r.data.empty_intakes || "",
        cta_new: r.data.cta_new || "",
      }))
      .catch(() => { /* silent */ });
  }, []);

  const savePageMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.put(`${API}/api/admin/atelier/page-meta`, pageMeta);
      toast.success("Textes de la page Atelier enregistrés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingMeta(false);
    }
  };

  const list = useMemo(() => {
    let arr = atelierIntakes || [];
    if (technicianFilter !== "all") arr = arr.filter((i) => i.technician_name === technicianFilter);
    return arr;
  }, [atelierIntakes, technicianFilter]);

  const counts = useMemo(
    () => ({
      total: atelierIntakes.length,
      reception: atelierIntakes.filter((i) => i.status === "reception").length,
      in_progress: atelierIntakes.filter((i) => i.status === "in_progress").length,
      completed: atelierIntakes.filter((i) => i.status === "completed").length,
    }),
    [atelierIntakes]
  );

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={ClipboardList} label="Total fiches" value={counts.total} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={Package} label="Réception" value={counts.reception} color="text-blue-400" accent="bg-blue-500/10" />
        <StatCard icon={Wrench} label="En cours" value={counts.in_progress} color="text-amber-400" accent="bg-amber-500/10" />
        <StatCard icon={FileText} label="Terminés" value={counts.completed} color="text-emerald-400" accent="bg-emerald-500/10" />
      </div>

      {/* Page copy editor (public-facing atelier page) */}
      <section className="bg-[#121212] border border-white/[0.06] rounded-lg p-5" data-testid="admin-atelier-page-meta">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Megaphone size={16} className="text-[#FF3B30]" />
            <h3 className="text-white font-bold">Textes de la page Atelier</h3>
          </div>
          <button
            onClick={savePageMeta}
            disabled={savingMeta}
            data-testid="admin-atelier-page-meta-save"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider"
          >
            {savingMeta ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {savingMeta ? "…" : "Enregistrer"}
          </button>
        </div>
        <p className="text-white/50 text-xs mb-4">
          Ces textes s&apos;affichent sur <code className="text-white/80">/atelier</code> côté technicien. Les fiches véhicules elles-mêmes se gèrent plus bas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PAGE_META_FIELDS.map((f) => (
            <div key={f.key} className={f.key === "welcome_top" || f.key === "empty_intakes" ? "md:col-span-2" : ""}>
              <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{f.label}</label>
              <input
                type="text"
                value={pageMeta[f.key]}
                onChange={(e) => setPageMeta({ ...pageMeta, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                data-testid={`admin-atelier-meta-${f.key}`}
                className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Config */}
      <SectionCard title="Configuration atelier" icon={Lock}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Mot de passe actuel</label>
            <input
              type="text" value={technicianPassword} readOnly
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-white/50 text-xs uppercase tracking-wider mb-1.5">Nouveau mot de passe</label>
            <input
              type="text" value={newTechnicianPassword}
              onChange={(e) => setNewTechnicianPassword(e.target.value)}
              placeholder="Entrez un nouveau mot de passe..."
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
            />
          </div>
          <button
            onClick={updateTechnicianPassword}
            className="bg-[#FF3B30] hover:bg-[#E6352B] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors h-[38px]"
          >
            Mettre à jour
          </button>
        </div>
        <p className="text-white/40 text-xs mt-3">
          Les techniciens accèdent à l'espace atelier via <span className="text-white/70 font-mono">/atelier</span> avec leur prénom et ce mot de passe.
        </p>
      </SectionCard>

      {/* Filters */}
      <SectionCard title="Filtres" icon={Filter} iconColor="text-white/50">
        <div className="flex flex-wrap gap-3 items-center">
          <SearchInput
            value={atelierSearch}
            onChange={(e) => { setAtelierSearch(e.target.value); fetchAtelierIntakes(atelierStatusFilter, e.target.value); }}
            placeholder="Rechercher (plaque, client, technicien...)"
            icon={Search}
          />
          <select
            value={technicianFilter}
            onChange={(e) => setTechnicianFilter(e.target.value)}
            className="bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm h-9"
          >
            <option value="all">Tous techniciens</option>
            {technicians.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { key: "", label: "Tous statuts" },
            { key: "reception", label: "Réception" },
            { key: "in_progress", label: "En cours" },
            { key: "completed", label: "Terminé" },
          ].map((s) => (
            <FilterPill
              key={s.key || "all"}
              active={atelierStatusFilter === s.key}
              onClick={() => { setAtelierStatusFilter(s.key); fetchAtelierIntakes(s.key, atelierSearch); }}
            >
              {s.label}
            </FilterPill>
          ))}
        </div>
      </SectionCard>

      {/* Intakes table */}
      <SectionCard title={`Fiches de prise en charge (${list.length})`} icon={FileText}>
        {list.length === 0 ? (
          <EmptyState icon={FileText} title="Aucune fiche" description="Les fiches créées par les techniciens apparaîtront ici." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left">
              <thead className="border-b border-white/[0.06]">
                <tr className="text-white/40 text-[11px] uppercase tracking-wider">
                  <th className="py-3 pr-4">Plaque</th>
                  <th className="py-3 pr-4">Véhicule</th>
                  <th className="py-3 pr-4">Client</th>
                  <th className="py-3 pr-4">Technicien</th>
                  <th className="py-3 pr-4">Statut</th>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((intake) => {
                  const cfg = ATELIER_STATUS[intake.status] || ATELIER_STATUS.reception;
                  return (
                    <tr key={intake.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 pr-4">
                        <span className="font-mono font-bold text-[#FF3B30] text-sm">{intake.plate}</span>
                      </td>
                      <td className="py-3 pr-4 text-white text-sm">
                        {intake.brand} {intake.model}
                        {intake.mileage > 0 && (
                          <span className="text-white/40 text-xs ml-1">
                            ({intake.mileage.toLocaleString()} km)
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-white/80 text-sm">{intake.client_name || "—"}</td>
                      <td className="py-3 pr-4 text-cyan-400 text-sm font-medium">{intake.technician_name}</td>
                      <td className="py-3 pr-4"><StatusBadge config={cfg} /></td>
                      <td className="py-3 pr-4 text-white/50 text-xs">
                        {new Date(intake.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="py-3 pr-0">
                        <div className="flex gap-1.5 justify-end">
                          <ActionButton icon={Eye} label="Voir" color="blue" onClick={() => setSelectedAtelierIntake(intake)} />
                          <ActionButton icon={Download} label="PDF" color="green" onClick={() => generateIntakePDF(intake)} />
                          <ActionButton icon={Trash2} label="" color="red" onClick={() => deleteAtelierIntake(intake.id)} title="Supprimer" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Intake Detail Modal */}
      {selectedAtelierIntake && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#141414]/95 backdrop-blur border-b border-white/[0.06] p-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-[#FF3B30] text-lg">{selectedAtelierIntake.plate}</span>
                <StatusBadge config={ATELIER_STATUS[selectedAtelierIntake.status] || ATELIER_STATUS.reception} />
              </div>
              <button onClick={() => setSelectedAtelierIntake(null)} className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Vehicle */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Car size={15} className="text-[#FF3B30]" /> Véhicule
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-white/40 text-xs uppercase tracking-wider">Marque/Modèle</p><p className="text-white">{selectedAtelierIntake.brand} {selectedAtelierIntake.model || "—"}</p></div>
                  <div><p className="text-white/40 text-xs uppercase tracking-wider flex items-center gap-1"><Gauge size={11} /> Kilométrage</p><p className="text-white">{selectedAtelierIntake.mileage?.toLocaleString() || 0} km</p></div>
                  <div><p className="text-white/40 text-xs uppercase tracking-wider flex items-center gap-1"><Fuel size={11} /> Carburant</p><p className="text-white">{selectedAtelierIntake.fuel_level || "—"}</p></div>
                  <div><p className="text-white/40 text-xs uppercase tracking-wider flex items-center gap-1"><UserCog size={11} /> Technicien</p><p className="text-cyan-400 font-medium">{selectedAtelierIntake.technician_name}</p></div>
                </div>
              </div>
              {/* Client */}
              {(selectedAtelierIntake.client_name || selectedAtelierIntake.client_phone) && (
                <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <User size={15} className="text-blue-400" /> Client
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-white/40 text-xs uppercase tracking-wider">Nom</p><p className="text-white">{selectedAtelierIntake.client_name || "—"}</p></div>
                    <div><p className="text-white/40 text-xs uppercase tracking-wider flex items-center gap-1"><Phone size={11} /> Téléphone</p><p className="text-white">{selectedAtelierIntake.client_phone || "—"}</p></div>
                  </div>
                </div>
              )}
              {/* Reception state */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <ClipboardList size={15} className="text-purple-400" /> État à la réception
                </h4>
                <div className="space-y-3 text-sm">
                  {selectedAtelierIntake.exterior_condition && (<div><p className="text-white/40 text-xs uppercase tracking-wider">État extérieur</p><p className="text-white/90">{selectedAtelierIntake.exterior_condition}</p></div>)}
                  {selectedAtelierIntake.interior_condition && (<div><p className="text-white/40 text-xs uppercase tracking-wider">État intérieur</p><p className="text-white/90">{selectedAtelierIntake.interior_condition}</p></div>)}
                  {selectedAtelierIntake.items_in_vehicle && (<div><p className="text-white/40 text-xs uppercase tracking-wider">Objets présents</p><p className="text-white/90">{selectedAtelierIntake.items_in_vehicle}</p></div>)}
                  {selectedAtelierIntake.initial_observations && (<div><p className="text-white/40 text-xs uppercase tracking-wider">Observations initiales</p><p className="text-white/90">{selectedAtelierIntake.initial_observations}</p></div>)}
                </div>
              </div>
              {/* Intervention */}
              {(selectedAtelierIntake.work_performed || selectedAtelierIntake.recommendations || selectedAtelierIntake.final_observations) && (
                <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Wrench size={15} className="text-emerald-400" /> Intervention
                  </h4>
                  <div className="space-y-3 text-sm">
                    {selectedAtelierIntake.work_performed && (<div><p className="text-white/40 text-xs uppercase tracking-wider">Travaux effectués</p><p className="text-white/90">{selectedAtelierIntake.work_performed}</p></div>)}
                    {selectedAtelierIntake.recommendations && (
                      <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3">
                        <p className="text-amber-400 text-xs uppercase tracking-wider flex items-center gap-1 font-medium"><AlertTriangle size={11} /> Recommandations</p>
                        <p className="text-white/90 mt-1">{selectedAtelierIntake.recommendations}</p>
                      </div>
                    )}
                    {selectedAtelierIntake.next_service_suggestion && (<div><p className="text-white/40 text-xs uppercase tracking-wider">Prochaine révision</p><p className="text-white/90">{selectedAtelierIntake.next_service_suggestion}</p></div>)}
                    {selectedAtelierIntake.final_observations && (<div><p className="text-white/40 text-xs uppercase tracking-wider">Observations finales</p><p className="text-white/90">{selectedAtelierIntake.final_observations}</p></div>)}
                  </div>
                </div>
              )}
              {/* Photos gallery */}
              {Array.isArray(selectedAtelierIntake.photos) && selectedAtelierIntake.photos.length > 0 && (
                <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4" data-testid="admin-atelier-photos">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Camera size={15} className="text-cyan-400" /> Photos de suivi ({selectedAtelierIntake.photos.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {selectedAtelierIntake.photos.map((url, i) => (
                      <a
                        key={`${url}-${i}`}
                        href={buildImageUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-video bg-[#0A0A0A] border border-white/[0.06] rounded overflow-hidden hover:border-[#FF3B30]/40 transition-colors"
                        title="Ouvrir en grand"
                      >
                        <img src={buildImageUrl(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-center text-white/40 text-[11px] space-y-0.5">
                <p className="flex items-center gap-1 justify-center"><Calendar size={11} /> Créé le {new Date(selectedAtelierIntake.created_at).toLocaleString("fr-FR")}</p>
                {selectedAtelierIntake.completed_at && (<p>Terminé le {new Date(selectedAtelierIntake.completed_at).toLocaleString("fr-FR")}</p>)}
              </div>
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => generateIntakePDF(selectedAtelierIntake)}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-md text-sm font-semibold transition-colors"
                >
                  <Download size={15} /> Télécharger PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtelierTab;
